import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Autocomplete from "@mui/material/Autocomplete";
import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import CommonDataService from "../../services/common";
import {
	AllowedUsergroup,
	parseDate,
	GetIndexByValue,
	clean,
	parseNumber,
	CDNWebsite,
} from "../../helpers/common";
import {
	setCurrentPageSize,
	setLoading,
	setModule,
} from "../../store/actions/commonAction";

import MessageDialog from "../MessageDialog";
import DeleteDialog from "../DeleteDialog";
import Pager from "../Pager";

export default function GalleryList() {
	const [searchParams, setSearchParams] = useSearchParams();

	let currentPage = clean(searchParams.get("page"));
	if (currentPage === "") {
		currentPage = 1;
	}

	let s = clean(searchParams.get("s"));
	if (s === "") {
		s = "g.publish_time";
	}

	let d = clean(searchParams.get("d"));
	if (d === "") {
		d = "desc";
	}

	const params = useRef();
	const name = useRef(clean(searchParams.get("name")));
	const active = useRef(clean(searchParams.get("active")));
	const orderBy = useRef(s);
	const order = useRef(d);

	const [rows, setRows] = useState([]);
	const [count, setCount] = useState(0);
	const [message, setMessage] = useState("");
	const [error, setError] = useState(false);
	const [del, setDel] = useState(false);
	const [delID, setDelID] = useState(0);
	const [page, setPage] = useState(parseNumber(currentPage));

	const [offices, setOffices] = useState([]);
	const [idOffice, setIdOffice] = useState(clean(searchParams.get("office")));
	const [idOfficeAC, setIdOfficeAC] = useState(null);

	const currentPageSize = useSelector(
		(state) => state.commonReducer.currentPageSize
	);

	const userGroup = useSelector((state) => state.commonReducer.user.usergroup);
	const orders = useSelector((state) => state.commonReducer.orders);
	const pageSizes = useSelector((state) => state.commonReducer.pageSizes);

	const dispatch = useDispatch();

	const sorts = [
		{
			label: "Publish Time",
			value: "g.publish_time",
		},
		{
			label: "Status",
			value: "g.active",
		},
	];

	const LoadData = useCallback(() => {
		let pars = {
			name: name.current,
			active: active.current,
			office: idOffice,
			s: orderBy.current,
			d: order.current,
			page: page,
			perpage: currentPageSize,
		};

		dispatch(setLoading(true));
		CommonDataService.getGalleries(pars)
			.then((res) => {
				setRows(res.data);
				setCount(res.data.total_pages);
				dispatch(setLoading(false));
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	}, [dispatch, idOffice, page, currentPageSize]);

	useEffect(() => {
		LoadData();
	}, [LoadData, dispatch, idOffice, page, currentPageSize]);

	const LoadSelect = useCallback(() => {
		if (AllowedUsergroup(userGroup, "superuser-website")) {
			CommonDataService.getOffices().then((res) => {
				setOffices(res.data);
			});
		}
	}, [userGroup]);

	useEffect(() => {
		LoadSelect();
	}, [LoadSelect]);

	useEffect(() => {
		setIdOfficeAC(GetIndexByValue(offices, idOffice));
	}, [offices, idOffice]);

	useEffect(() => {
		dispatch(setModule("Gallery"));
	}, [dispatch]);

	const handleSubmit = (event) => {
		event.preventDefault();
		const data = new FormData(event.currentTarget);
		name.current = data.get("name");
		active.current = data.get("active");
		orderBy.current = data.get("orderBy");
		order.current = data.get("order");
		setPage(1);
		setSearchParams(searchParams);
		LoadData();
	};

	const OnChangeOffices = (e, val) => {
		if (val !== null) {
			setIdOffice(val.id);
		} else {
			setIdOffice("");
		}

		setPage(1);
	};

	const confirmDelete = (id, title) => {
		setDel(true);
		setDelID(id);
		setMessage(title);
	};

	const Delete = () => {
		setDel(false);
		params.current = {};

		dispatch(setLoading(true));
		CommonDataService.deleteGallery(delID)
			.then((res) => {
				LoadData();
			})
			.catch(function (error) {
				setMessage(error.response.data.message);
				setError(true);
				dispatch(setLoading(false));
			});
	};

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
					<Box component="form" onSubmit={(e) => handleSubmit(e)}>
						<Typography variant="h5">Search Gallery Form</Typography>

						<Grid container spacing={3}>
							{AllowedUsergroup(userGroup, "superuser-website") && (
								<Grid item xs={12} sm={12}>
									<Autocomplete
										options={offices}
										getOptionLabel={(option) => option.name}
										fullWidth={true}
										onChange={OnChangeOffices}
										value={idOfficeAC}
										sx={{ minWidth: 100 }}
										renderInput={(params) => (
											<TextField
												variant="standard"
												{...params}
												label="Office"
											/>
										)}
									/>
								</Grid>
							)}

							<Grid item xs={12} sm={6}>
								<TextField
									label="Search by title"
									id="name"
									name="name"
									fullWidth
									defaultValue={name.current}
									variant="standard"
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="id-active-label">Status</InputLabel>
									<Select
										id="active"
										name="active"
										labelId="id-active-label"
										defaultValue={active.current}
									>
										<MenuItem value="">
											<em>All</em>
										</MenuItem>
										<MenuItem value="1">Active</MenuItem>
										<MenuItem value="0">Inactive</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={3}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="orderbylabel">Order by</InputLabel>
									<Select
										id="orderBy"
										name="orderBy"
										labelId="orderbylabel"
										label="Order By"
										defaultValue={orderBy.current}
									>
										{sorts.map((option, index) => (
											<MenuItem key={index} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={3}>
								<FormControl
									fullWidth={true}
									variant="standard"
									sx={{ minWidth: 100 }}
								>
									<InputLabel id="orderlabel">Sort</InputLabel>
									<Select
										id="order"
										name="order"
										labelId="orderlabel"
										label="Sort"
										defaultValue={order.current}
									>
										{orders.map((option, index) => (
											<MenuItem key={index} value={option.value}>
												{option.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} sm={6}></Grid>

							<Grid item xs={6} sm={6}>
								<Button
									type="submit"
									variant="contained"
									startIcon={<SearchIcon />}
								>
									Search
								</Button>
							</Grid>
							<Grid item xs={6} sm={6} container justifyContent="flex-end">
								<Button
									variant="contained"
									component={Link}
									to="/addgallery"
									startIcon={<AddIcon />}
								>
									Create
								</Button>
							</Grid>
						</Grid>
					</Box>
				</Paper>
			</Grid>

			<Grid item xs={12}>
				<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
					<Typography variant="h4" gutterBottom component="div">
						Gallery List
					</Typography>

					<TableContainer component={Paper}>
						<Table sx={{}} size="small">
							<TableHead>
								<TableRow>
									<TableCell width={5}>No.</TableCell>
									<TableCell>&nbsp;</TableCell>
									<TableCell>Office</TableCell>
									<TableCell>Title</TableCell>
									<TableCell align="center">Publish Time</TableCell>
									<TableCell align="center">Status</TableCell>
									<TableCell align="center">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{rows.Data &&
									rows.Data.map((row, index) => (
										<TableRow
											key={index}
											sx={{
												"&:last-child td, &:last-child th": { border: 0 },
											}}
										>
											<TableCell align="right">
												{rows.first_no + index}
											</TableCell>
											<TableCell>
												<img
													alt=""
													height="120"
													src={CDNWebsite() + row.thumb}
												/>
											</TableCell>
											<TableCell>{row.office}</TableCell>
											<TableCell>{row.title}</TableCell>
											<TableCell align="center">
												{parseDate(row.publish_time)}
											</TableCell>
											<TableCell align="center">
												{row.active ? "yes" : "no"}
											</TableCell>
											<TableCell align="center">
												<Link
													to={
														"/editgallery/" +
														row.id +
														"?name=" +
														name.current +
														"&active=" +
														active.current +
														"&office=" +
														idOffice +
														"&s=" +
														orderBy.current +
														"&d=" +
														order.current +
														"&page=" +
														page
													}
													className="badge badge-warning"
												>
													<EditIcon fontSize="small" />
												</Link>
												<DeleteIcon
													fontSize="small"
													onClick={() => confirmDelete(row.id, row.title)}
												/>
											</TableCell>
										</TableRow>
									))}
							</TableBody>

							<Pager
								colSpan={7}
								count={count}
								dispatch={dispatch}
								page={page}
								pageSizes={pageSizes}
								currentPageSize={currentPageSize}
								setPage={setPage}
								setCurrentPageSize={setCurrentPageSize}
							/>
						</Table>
					</TableContainer>

					<MessageDialog
						show={error}
						message={message}
						dialogClose={setError}
					/>

					<DeleteDialog
						show={del}
						message={message}
						dialogClose={setDel}
						Delete={Delete}
					/>
				</Paper>
			</Grid>
		</Grid>
	);
}
